import { NativeDateAdapter } from '@angular/material/core';
import { Injectable } from '@angular/core';

@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  // displayFormat is `Object` in NativeDateAdapter's signature (an external library boundary);
  // narrowing to a single TS type is not possible without breaking the override.
  override format(date: Date, displayFormat: unknown): string {
    if (displayFormat === 'input') {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    }
    return date.toDateString();
  }
}

export const APP_DATE_FORMATS = {
  parse: {
    dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { year: 'numeric', month: 'numeric' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

@Injectable({
  providedIn: 'root',
})
export class DateHelper {
  calculateDayDiff(startDate: Date, endDate: Date): number {
    return Math.floor(
      (Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()) -
        Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())) /
        (1000 * 60 * 60 * 24)
    );
  }

  isWeekDay(date: Date): boolean {
    return date.getDay() != 6 && date.getDay() != 0;
  }

  timeIsBefore(time: string, controlTime: string): boolean {
    return time < controlTime;
  }

  timeIsAfter(time: string, controlTime: string): boolean {
    return time > controlTime;
  }

  /**
   * Formats a date as a 'YYYY-MM-DD' string using local date parts.
   * This avoids the BST/UTC timezone shift that occurs when JSON.stringify
   * converts a midnight-local Date to UTC (e.g. 2026-04-13T00:00:00+01:00
   * becomes 2026-04-12T23:00:00Z, shifting the date back by one day).
   *
   * Accepts either a Date (from a datepicker selection) or an ISO/'YYYY-MM-DD'
   * string (an event's existing eventDate, which round-trips through the dialog
   * unchanged when the user edits other fields without re-picking the date).
   * A bare 'YYYY-MM-DD' string is already in the target format, so it is parsed
   * as a local calendar date to avoid the UTC midnight shift that `new Date(str)`
   * would otherwise introduce.
   */
  formatDateOnly(date: Date | string): string {
    const resolved = this.coerceToLocalDate(date);
    const year = resolved.getFullYear();
    const month = String(resolved.getMonth() + 1).padStart(2, '0');
    const day = String(resolved.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private coerceToLocalDate(date: Date | string): Date {
    if (date instanceof Date) {
      return date;
    }
    // A 'YYYY-MM-DD' string parsed via `new Date()` is treated as UTC midnight,
    // which can roll back a day in negative-offset zones. Build a local-midnight
    // Date from the parts instead so the calendar date is preserved.
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    return new Date(date);
  }
}
